'use client'

import { useCallback, useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { ChevronDown } from 'lucide-react'

import type { RenderLink } from '@ui/web/utils'
import {
  Button,
  Skeleton,
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@ui/web/components'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../context'
import { useColumns } from './columns'
import { Empty } from './empty'
import { RecipientDialog } from './recipient-dialog'

const SENT_COLLECTION_PAGE_SIZE = 10

interface SentCollectionPageProps {
  type: 'web' | 'school'
  className?: string
  renderLinkCollection?: RenderLink
  renderLinkEmptyState?: RenderLink
}

export function SentCollectionPage({
  type,
  className,
  renderLinkCollection,
  renderLinkEmptyState,
}: SentCollectionPageProps) {
  const trpc = useSharedTRPC()

  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    ...trpc.myBookCollection.getAllSentBookCollections.infiniteQueryOptions(
      { pageSize: SENT_COLLECTION_PAGE_SIZE },
      { getNextPageParam: (lastPage) => lastPage.nextCursor, initialCursor: 1 },
    ),
  })

  const allRows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data])

  const [selectedSentId, setSelectedSentId] = useState<string | null>(null)
  const onOpenRecipients = useCallback((id: string) => setSelectedSentId(id), [])
  const selectedRow = selectedSentId ? allRows.find((r) => r.id === selectedSentId) : null

  const columns = useColumns({ type, renderLink: renderLinkCollection, onOpenRecipients })

  const table = useReactTable({
    data: allRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  })

  if (isError) {
    return (
      <div
        className={cn(
          'srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-2 srd:py-8 srd:w-full srd:bg-neutrals-n-20',
          'srd:border-neutrals-n-20 srd:overflow-hidden srd:rounded-md srd:border',
          className,
        )}
      >
        <p className='srd:text-main-red-100'>Gagal memuat kiriman koleksi</p>
        <Button size='sm' onClick={() => void refetch()} variant='outline'>
          Coba lagi
        </Button>
      </div>
    )
  }

  if (isPending) {
    return (
      <div
        className={cn(
          'srd:w-full srd:border-neutrals-n-20 srd:overflow-hidden srd:rounded-md srd:border srd:bg-neutrals-white',
          className,
        )}
      >
        <TableComponent>
          <TableHeader className='srd:bg-neutrals-nvar-10'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className='srd:text-nowrap'>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {Array.from({ length: SENT_COLLECTION_PAGE_SIZE }, (_, i) => (
              <TableRow key={i}>
                {columns.map((col, colIndex) => (
                  <TableCell key={col.id ?? colIndex}>
                    <Skeleton className='srd:h-6 srd:w-full' />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </TableComponent>
      </div>
    )
  }

  if (allRows.length === 0) {
    return (
      <div
        className={cn(
          'srd:w-full srd:border-neutrals-n-20 srd:overflow-hidden srd:rounded-md srd:border srd:bg-neutrals-white',
          className,
        )}
      >
        <TableComponent>
          <TableHeader className='srd:bg-neutrals-nvar-10'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className='srd:text-nowrap'>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            <TableRow className='srd:data-[state=selected]:bg-transparent srd:hover:bg-transparent'>
              <TableCell colSpan={columns.length} className='srd:text-center srd:py-4'>
                <Empty type={type} renderLink={renderLinkEmptyState} className='srd:text-complementary-black' />
              </TableCell>
            </TableRow>
          </TableBody>
        </TableComponent>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'srd:w-full srd:border-neutrals-n-20 srd:overflow-hidden srd:rounded-md srd:border srd:bg-neutrals-white',
        className,
      )}
    >
      <TableComponent>
        <TableHeader className='srd:bg-neutrals-nvar-10'>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className='srd:text-nowrap'>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))}
          {hasNextPage && (
            <TableRow>
              <TableCell colSpan={columns.length} className='srd:text-center srd:py-4'>
                <Button size='sm' onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
                  {isFetchingNextPage ? 'Memuat...' : 'Muat lebih banyak'}
                  <ChevronDown className='srd:size-4 srd:ml-2' />
                </Button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </TableComponent>

      {selectedRow && (
        <RecipientDialog
          data={selectedRow.sentBookCollectionToUsers}
          open={true}
          onOpenChange={(open) => {
            if (!open) setSelectedSentId(null)
          }}
        />
      )}
    </div>
  )
}
